import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  Plus,
  Minus,
  Target,
  SignOut,
  Lightning,
  TrendUp,
  Users,
  ChartLineUp,
  Trophy,
  EnvelopeSimple,
  Clock,
  House,
  Crown,
  Lock,
  Heart,
  Barcode,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { ShiftComparisonCard } from "./ManagerDashboard";
import useNotificationCue from "../hooks/useNotificationCue";
import tap from "../utils/haptics";
import { useTheme } from "../ThemeContext";
import DailyPasscodeCard from "../components/DailyPasscodeCard";
import CouponHeaderPill from "../components/CouponHeaderPill";
import FloatingPops from "../components/FloatingPops";
import { enqueueTap, flushQueue, getQueueSize } from "../utils/captureQueue";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORE_TIMEZONE = "America/Denver";

const getTodayDate = () => {
  return new Date().toLocaleDateString("en-CA", { timeZone: STORE_TIMEZONE });
};

// Returns {h,m,s, totalSec} in store MST timezone
const getMSTNow = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: STORE_TIMEZONE,
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date());
  const h = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
  const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
  const s = parseInt(parts.find((p) => p.type === "second").value, 10);
  return { h, m, s, totalSec: h * 3600 + m * 60 + s };
};

// Parse "HH:MM" to seconds-since-midnight
const parseTimeToSec = (str, fallbackSec) => {
  if (!str || typeof str !== "string") return fallbackSec;
  const [h, m] = str.split(":").map((n) => parseInt(n, 10));
  if (isNaN(h) || isNaN(m)) return fallbackSec;
  return h * 3600 + m * 60;
};

// Format "HH:MM" -> "2 PM" / "2:30 PM"
const formatTimeLabel = (str) => {
  if (!str) return "";
  const [hStr, mStr] = str.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (isNaN(h) || isNaN(m)) return str;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// Returns current shift window info. Times come from manager-editable settings.
const getShiftWindow = (totalSec, hours) => {
  const open = parseTimeToSec(hours?.open_time, 9 * 3600);
  const cp1 = parseTimeToSec(hours?.cp1_time, 14 * 3600);
  const cp2 = parseTimeToSec(hours?.cp2_time, 17 * 3600);
  const cp3 = parseTimeToSec(hours?.cp3_time, 20 * 3600);
  const close = parseTimeToSec(hours?.close_time, 21 * 3600);
  const cp1Label = `${formatTimeLabel(hours?.cp1_time || "14:00")} Reading`;
  const cp2Label = `${formatTimeLabel(hours?.cp2_time || "17:00")} Reading`;
  const cp3Label = `${formatTimeLabel(hours?.cp3_time || "20:00")} Reading`;
  const closeLabel = `Close (${formatTimeLabel(hours?.close_time || "21:00")})`;

  if (totalSec < open) {
    return { label: "Before Open", nextLabel: cp1Label, nextAt: cp1, windowStart: open, windowEnd: cp1, active: false, isClosed: true, beforeOpen: true, openAt: open };
  }
  if (totalSec < cp1) {
    return { label: "Morning Push", nextLabel: cp1Label, nextAt: cp1, windowStart: open, windowEnd: cp1, active: true, isClosed: false };
  }
  if (totalSec < cp2) {
    return { label: "Afternoon Shift", nextLabel: cp2Label, nextAt: cp2, windowStart: cp1, windowEnd: cp2, active: true, isClosed: false };
  }
  if (totalSec < cp3) {
    return { label: "Evening Shift", nextLabel: cp3Label, nextAt: cp3, windowStart: cp2, windowEnd: cp3, active: true, isClosed: false };
  }
  if (totalSec < close) {
    return { label: "Final Push", nextLabel: closeLabel, nextAt: close, windowStart: cp3, windowEnd: close, active: true, isClosed: false };
  }
  return { label: "Store Closed", nextLabel: null, nextAt: null, windowStart: close, windowEnd: 24 * 3600, active: false, isClosed: true, openAt: open };
};

const formatCountdown = (remainingSec) => {
  if (remainingSec <= 0) return "0:00";
  const h = Math.floor(remainingSec / 3600);
  const m = Math.floor((remainingSec % 3600) / 60);
  const s = remainingSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

// Helper: progress bar class
const getProgressClass = (atGoal, rate, threshold = 0.2) => {
  if (atGoal) return "at-goal";
  if (rate < threshold) return "critical";
  return "below-goal";
};

// Helper: rank badge class
const getRankBadgeClass = (idx) => {
  if (idx === 0) return "bg-amber-400 text-white";
  if (idx === 1) return "bg-slate-300 text-slate-700";
  if (idx === 2) return "bg-amber-700 text-white";
  return "bg-slate-100 text-slate-500";
};

// Celebration & motivation messages
const CELEBRATIONS = {
  goal_hit: [
    "We hit our goal! Keep the energy going!",
    "35% reached! Amazing teamwork!",
    "Goal crushed! Let's push for 50%!",
  ],
  stretch_hit: [
    "50% STRETCH GOAL! You're all-stars!",
    "Incredible! 50% captured - legendary shift!",
    "STRETCH GOAL SMASHED! Wow Crew = unstoppable!",
  ],
  motivational: [
    "Almost there! Every email counts!",
    "Let's pick it up - we've got this!",
    "Stay focused, stay sharp - goal is within reach!",
    "Keep pushing - the finish line is close!",
    "You're making a difference with every capture!",
  ],
  individual: [
    "Nice one! Keep it rolling!",
    "Another one in the bag!",
    "You're on fire!",
    "Solid work!",
  ],
  milestone_5: ["5 emails! Great start!", "High five - that's 5 captures!"],
  milestone_10: ["10 emails! Double digits - you're crushing it!", "10 and counting!"],
  milestone_20: ["20 emails! You're a capture machine!", "20! Outstanding!"],
};

const getRandomMsg = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Tiny per-tap confetti — fires off the +1 button on every successful
// capture (FB default theme only). Way smaller than `burstConfetti` so
// it celebrates without distracting from the next tap. (User ask
// 2026-05-04 "add some cool stuff across the five below theme".)
const microConfetti = (originX = 0.5, originY = 0.65) => {
  confetti({
    particleCount: 18,
    spread: 55,
    startVelocity: 28,
    decay: 0.92,
    scalar: 0.7,
    ticks: 50,
    origin: { x: originX, y: originY },
    colors: ["#0066FF", "#FACC15", "#EC4899", "#16A34A", "#FFFFFF"],
    disableForReducedMotion: true,
  });
};

// Fire a celebratory confetti burst for 5/10/15/20 milestones
const burstConfetti = () => {
  const count = 220;
  const defaults = { origin: { y: 0.7 }, colors: ["#0066FF", "#38bdf8", "#fde047", "#34d399", "#f472b6"] };
  const fire = (particleRatio, opts) => {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio),
    });
  };
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.1 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// Fireworks sequence for the 25-email milestone. Accepts an optional
// `colors` array so themed presets (e.g. Patriotic) can tint the bursts
// red/white/blue without needing a separate implementation.
const fireFireworks = (colors = null) => {
  const duration = 4000;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };
  if (colors && colors.length) defaults.colors = colors;
  const rand = (min, max) => Math.random() * (max - min) + min;
  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }
    const particleCount = 60 * (timeLeft / duration);
    confetti({ ...defaults, particleCount, origin: { x: rand(0.1, 0.4), y: Math.random() - 0.2 } });
    confetti({ ...defaults, particleCount, origin: { x: rand(0.6, 0.9), y: Math.random() - 0.2 } });
  }, 250);
};

function ShiftClock({ hours }) {
  const [now, setNow] = useState(getMSTNow());

  useEffect(() => {
    const tick = () => setNow(getMSTNow());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const window = getShiftWindow(now.totalSec, hours);
  const remaining = window.nextAt !== null ? Math.max(0, window.nextAt - now.totalSec) : 0;
  const windowLen = window.windowEnd - window.windowStart;
  const elapsed = Math.max(0, Math.min(windowLen, now.totalSec - window.windowStart));
  const pct = windowLen > 0 ? (elapsed / windowLen) * 100 : 0;
  const openLabel = formatTimeLabel(hours?.open_time || "09:00");

  const currentTimeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: STORE_TIMEZONE, hour: "numeric", minute: "2-digit", hour12: true,
  }).format(new Date());

  return (
    <div className="dashboard-card" data-testid="shift-clock">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock weight="bold" className="w-5 h-5" style={{ color: "var(--fb-blue)" }} />
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Shift Clock</span>
        </div>
        <span className="text-xs text-slate-400 font-medium" data-testid="shift-clock-now">{currentTimeStr}</span>
      </div>

      <div className="flex items-end justify-between mb-3">
        <div>
          <p className="text-xs text-slate-500 font-medium mb-1">Current Window</p>
          <p className="text-2xl font-black text-slate-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }} data-testid="shift-window-label">
            {window.label}
          </p>
        </div>
        {window.active && window.nextAt !== null && (
          <div className="text-right">
            <p className="text-xs text-slate-500 font-medium mb-1">Until {window.nextLabel}</p>
            <p className="text-2xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: remaining < 900 ? "#d97706" : "var(--fb-blue)" }} data-testid="shift-countdown">
              {formatCountdown(remaining)}
            </p>
          </div>
        )}
      </div>

      {window.active ? (
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: "var(--fb-blue)" }} />
        </div>
      ) : (
        <p className="text-xs text-slate-400">
          {now.totalSec < parseTimeToSec(hours?.open_time, 9 * 3600) ? `Doors open at ${openLabel}.` : "Shift captures are done for today. Great work!"}
        </p>
      )}
    </div>
  );
}

export default function CrewTallyPage({ user, crew, onLogout, onSwitchToManager, onOpenInbox, onGoHome }) {
  const [tallyData, setTallyData] = useState(null);
  const [wtdData, setWtdData] = useState(null);
  const [checkpointInfo, setCheckpointInfo] = useState([]);
  const [personalBest, setPersonalBest] = useState(null);
  const [tapping, setTapping] = useState(false);
  // Store visitors — crew from OTHER stores covering here today.
  // Merged into `crew` below so the tally +1 grid renders their card,
  // and their captures count toward THIS store (the tally POST already
  // uses the URL's store_id — the crew.home_store_id isn't referenced
  // on write). 2026-08-06 feature.
  const [visitors, setVisitors] = useState([]);
  useEffect(() => {
    const sid = user?.home_store_id;
    if (!sid) return undefined;
    // MST-anchored today — UTC-slice would roll forward at ~5 PM MST
    // and query tomorrow's empty visitor list, wiping the "on-floor"
    // display when the store hits close. (Same class of bug as
    // HomeScreen's Right Now reset, 2026-02-27.)
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/store-visitors/${sid}/${today}`);
        if (!cancelled) setVisitors(r.data?.visitors || []);
      } catch { /* stay empty on failure */ }
    })();
    return () => { cancelled = true; };
  }, [user?.home_store_id]);
  // Offline capture queue UI — number of taps waiting to sync.
  const [queueSize, setQueueSize] = useState(() => {
    try { return getQueueSize(); } catch { return 0; }
  });
  const [lastGoalState, setLastGoalState] = useState({ atGoal: false, atStretch: false });
  const [showCelebration, setShowCelebration] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Unread notifications (targeted manager adjustments, team blasts, etc.) drive the bell cue.
  const [notifUnread, setNotifUnread] = useState(0);
  // The mute toggle lives on the HomePage header — we just read the saved preference.
  const cueMuted = (() => { try { return localStorage.getItem("ecDashCueMuted") === "1"; } catch { return false; } })();
  // Fires a ding + haptic buzz whenever the notification unread count increments.
  useNotificationCue(notifUnread, { enabled: !cueMuted });
  const [showWtd, setShowWtd] = useState(false);
  // Daily Passcode moved from an in-page card to a header icon (user ask
  // 2026-07-02). We keep the same DailyPasscodeCard component mounted in
  // headless mode (`hideBar`) so all its scan/claim/barcode logic still
  // fires; tapping the header pill just opens the same slide-up sheet.
  const [passcodeSheetOpen, setPasscodeSheetOpen] = useState(false);
  const [storeHours, setStoreHours] = useState(null);
  // Heart Balloon counter removed 2026-05-19 with the campaign cleanup.

  // May the 4th glitch on the +1 tap. When the deep-space theme is active,
  // every successful capture briefly flashes a "−1" glitch overlay over the
  // big plus button before snapping back. The actual tally still increments
  // immediately — this is purely visual sass. (User ask 2026-05-04 "add a
  // glitching effect to collecting emails so when we push the plus it
  // subtracts as a glitch then adds that plus to make it fun".)
  const { settings: storeSettings } = useTheme();
  const may4Active = (storeSettings?.background_color || "").toUpperCase() === "#050418";
  // Patriotic preset detection — tints the 25-email milestone confetti
  // red/white/blue when active. Detected by the primary+accent combo the
  // Patriotic settings preset locks in (SettingsPage THEME_PRESETS).
  const patrioticActive =
    (storeSettings?.primary_color || "").toUpperCase() === "#DC2626" &&
    (storeSettings?.accent_color || "").toUpperCase() === "#2563EB";
  // FB default theme = anything that isn't the May 4 deep-space preset.
  // Drives the new playful pop / streak / odometer animations introduced
  // 2026-05-04. Gated this way so May 4's dedicated effects don't double up.
  const fbThemeActive = !may4Active;
  const [plusGlitching, setPlusGlitching] = useState(false);
  // Imperative ref into FloatingPops so we can fire a "+1" / "WOW!" pop on
  // every successful capture without re-rendering the host on every tap.
  const popsRef = useRef(null);
  // One-shot lightning sweep over the screen when the team first crosses
  // the daily goal line. Driven by a re-keyed div that auto-unmounts after
  // 700ms via the streakKey state below.
  const [streakKey, setStreakKey] = useState(0);
  // Balloon polling + tap handler removed 2026-05-19 with the campaign cleanup.

  const myId = user.crew_id;
  const myName = user.first_name;
  const today = getTodayDate();

  useEffect(() => {
    let cancelled = false;
    const loadHours = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (!cancelled) setStoreHours(res.data);
      } catch (e) {
        console.error("Error fetching settings:", e);
      }
    };
    loadHours();
    const id = setInterval(loadHours, 60000); // refresh every 60s so manager edits propagate
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // In-flight guard so overlapping poll + POST + poll don't stack up on
  // slow store wifi. When a fetch is already running, new tick requests are
  // silently dropped. When a successful POST returns fresh state, we bump
  // `skipNextFetchRef` so the immediately-following poll is skipped — the
  // POST response already contained the authoritative state, and running
  // another fetch right after just re-renders the big leaderboard twice
  // with the same data (which was the source of the "freeze when multiple
  // users enter data" report — overlapping setStates on a 14-card grid).
  const fetchingRef = useRef(false);
  const skipNextFetchRef = useRef(false);
  const fetchTally = useCallback(async () => {
    if (fetchingRef.current) return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchingRef.current = true;
    try {
      const [tallyRes, wtdRes, cpRes, bestRes, unreadRes, notifRes] = await Promise.all([
        axios.get(`${API}/tally/${today}`),
        axios.get(`${API}/wtd-summary`),
        axios.get(`${API}/checkpoints/${today}`),
        axios.get(`${API}/personal-best/${myId}`),
        axios.get(`${API}/inbox/${myId}/unread-count?is_manager=${user.is_manager || false}`),
        axios.get(`${API}/notifications/unread-count?crew_id=${myId}&is_manager=${user.is_manager || false}`),
      ]);
      setTallyData(tallyRes.data);
      setWtdData(wtdRes.data);
      setCheckpointInfo(cpRes.data);
      setPersonalBest(bestRes.data);
      setUnreadCount(unreadRes.data.unread || 0);
      setNotifUnread(notifRes.data.unread || 0);
    } catch (e) {
      console.error("Error fetching tally:", e);
    } finally {
      fetchingRef.current = false;
    }
  }, [today, myId, user.is_manager]);

  useEffect(() => {
    fetchTally();
    // Poll every 5s while visible. When the tab is backgrounded the
    // interval keeps firing (browsers throttle to 1Hz but don't kill
    // it) so we explicitly no-op the fetch on hidden to save battery +
    // cellular data. Fires once immediately on visibility-return so
    // the manager sees fresh data as soon as they come back to the app.
    // (Battery-drain fix 2026-08-01.)
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => {
        if (!document.hidden) fetchTally();
      }, 5000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVis = () => {
      if (document.hidden) { stop(); }
      else { fetchTally(); start(); }
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchTally]);

  // Offline-queue silent-sync. Drains stored taps when:
  //   1. The browser fires `online`
  //   2. Every 30s tick (covers cases where `online` fires but the
  //      backend is still warming up — we just retry)
  //   3. The queue grows via enqueueTap (subscribed event)
  // Each successful flush triggers a fresh fetchTally so the UI gets
  // the authoritative server numbers instead of relying on the
  // optimistic local bump.
  useEffect(() => {
    if (!myId) return undefined;
    const postOne = async (entry) => {
      return axios.post(`${API}/tally`, {
        crew_id: entry.crew_id,
        date: entry.date,
        action: entry.action,
        // 2026-02 auth guard — every queued tap was owned by `myId` at
        // enqueue time, so pass myId as the actor. Endpoint verifies
        // actor.id == crew_id (self-tap) OR actor is a manager+.
        actor_id: myId,
      });
    };
    let cancelled = false;
    const drain = async () => {
      try {
        const flushed = await flushQueue(postOne);
        if (cancelled) return;
        setQueueSize(getQueueSize());
        if (flushed > 0) {
          // Reconcile UI with server-authoritative numbers.
          fetchTally();
          // Big batches earn a confetti pop so a rough wifi day ends on
          // a win. Single catches get a quieter toast so it doesn't
          // feel spammy (Zebras can drop & restore every few minutes).
          if (flushed >= 5) {
            burstConfetti();
            toast.success(`🎉 Synced ${flushed} captures while you were offline!`, {
              duration: 3500,
            });
          } else {
            toast.success(`📡 Synced ${flushed} pending capture${flushed === 1 ? "" : "s"}`, {
              duration: 2000,
            });
          }
        }
      } catch { /* swallow — next tick will retry */ }
    };
    const onOnline = () => drain();
    const onQueueChange = (e) => setQueueSize(e?.detail?.size ?? getQueueSize());
    window.addEventListener("online", onOnline);
    window.addEventListener("wow:capture-queue-change", onQueueChange);
    // First-paint sweep + 30s polling
    drain();
    const tick = setInterval(drain, 30 * 1000);
    return () => {
      cancelled = true;
      clearInterval(tick);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("wow:capture-queue-change", onQueueChange);
    };
  }, [myId, fetchTally]);

  const handleTally = async (action) => {
    if (tapping) return;
    // Haptic already fired on pointer-down (see button handlers) for instant
    // feel. We only re-fire here for error paths below.
    // Block tallying when store is closed
    const now = getMSTNow();
    const win = getShiftWindow(now.totalSec, storeHours);
    if (win.isClosed) {
      if (win.beforeOpen) {
        toast.error(`Store opens at ${formatTimeLabel(storeHours?.open_time || "09:00")} — captures unlock then.`);
      } else {
        toast.error("Store is closed — no more email captures can be logged.");
      }
      return;
    }
    setTapping(true);
    try {
      // Retry once on transient network errors (flaky store wifi drops,
      // brief server restart, etc.) before surfacing an error to the crew.
      // Only network / 5xx retries — never a 4xx (store closed, invalid
      // request, etc.) which the user needs to see immediately.
      const postTally = async () => axios.post(`${API}/tally`, {
        crew_id: myId,
        date: today,
        action: action,
        actor_id: myId, // 2026-02 auth guard — self-tap, must match crew_id
      });
      let res;
      try {
        res = await postTally();
      } catch (first) {
        const status = first?.response?.status;
        const isTransient = !status || status >= 500;
        if (!isTransient) throw first;
        await new Promise((r) => setTimeout(r, 350));
        res = await postTally();
      }

      const prevData = tallyData;
      setTallyData(res.data);
      // The POST already carries the authoritative next state — skip the
      // poll that's about to fire in the next 5s cycle so we don't re-render
      // the leaderboard a second time with the exact same data. This was
      // the main cause of the "freezes when multiple users tap" report.
      skipNextFetchRef.current = true;

      // Broadcast to the rest of the app (esp. the HomePage "Collect
      // Emails" tile) so its snoozing/dancing personality can flip the
      // instant a scan lands instead of waiting for its own 30s poll
      // cycle. (User report 2026-02: "found a bug after we scan our
      // barcode the tile still reads snoozing — took a minute to
      // update, wasn't instant".)
      try {
        window.dispatchEvent(new CustomEvent("wow:tally-updated", {
          detail: {
            crew_id: myId,
            my_count: res.data?.my_count || (res.data?.crew_counts?.[myId] || 0),
            capture_rate: res.data?.capture_rate || 0,
            action,
          },
        }));
      } catch { /* ignore */ }

      // Server-detected milestone (5/10/15/20/25). The server also auto-blasts on 25.
      if (res.data.milestone === 25) {
        // Tint red/white/blue when the Patriotic theme is on so the
        // milestone matches the theme; otherwise let confetti use its
        // default rainbow palette.
        fireFireworks(patrioticActive ? ["#DC2626", "#FFFFFF", "#2563EB"] : null);
        toast.success(`🎆 You hit 25! The team's been notified.`, { duration: 4500 });
      } else if ([5, 10, 15, 20].includes(res.data.milestone)) {
        burstConfetti();
      }

      if (action === "add") {
        const newCount = res.data.my_count || (res.data.crew_counts?.[myId] || 0);

        if (newCount === 5) {
          triggerCelebration(getRandomMsg(CELEBRATIONS.milestone_5), "milestone");
        } else if (newCount === 10) {
          triggerCelebration(getRandomMsg(CELEBRATIONS.milestone_10), "milestone");
        } else if (newCount === 20) {
          triggerCelebration(getRandomMsg(CELEBRATIONS.milestone_20), "milestone");
        } else if (personalBest && newCount > personalBest.best_count && personalBest.best_count > 0) {
          triggerCelebration(`NEW PERSONAL BEST! ${newCount} emails - you beat your record of ${personalBest.best_count}!`, "goal");
        } else if (Math.random() < 0.25) {
          toast.success(getRandomMsg(CELEBRATIONS.individual), { duration: 1200 });
        } else {
          toast.success(`+1 email`, { duration: 800 });
        }

        const wasAtGoal = prevData?.at_goal || lastGoalState.atGoal;
        const wasAtStretch = prevData?.at_stretch || lastGoalState.atStretch;

        if (res.data.at_stretch && !wasAtStretch) {
          setTimeout(() => triggerCelebration(getRandomMsg(CELEBRATIONS.stretch_hit), "stretch"), 500);
          setLastGoalState({ atGoal: true, atStretch: true });
        } else if (res.data.at_goal && !wasAtGoal) {
          // Lightning sweep across the screen the instant the team hits
          // the daily goal. Re-keying triggers a fresh mount so the 700ms
          // animation runs cleanly even on back-to-back goal-stretch hits.
          // FB default theme only — May 4 has its own dedicated celebrations.
          if (fbThemeActive) setStreakKey((k) => k + 1);
          setTimeout(() => triggerCelebration(getRandomMsg(CELEBRATIONS.goal_hit), "goal"), 500);
          setLastGoalState({ ...lastGoalState, atGoal: true });
        }

        if (!res.data.at_goal && res.data.store_transactions > 0 && res.data.emails_needed_goal > 0) {
          const behindPct = res.data.capture_rate / res.data.goal_rate;
          if (behindPct < 0.5 && Math.random() < 0.15) {
            toast(`${res.data.emails_needed_goal} more to goal - ${getRandomMsg(CELEBRATIONS.motivational)}`, { duration: 3000 });
          }
        }
      }
    } catch (e) {
      // Network drop or 5xx after retry → save to offline queue and
      // optimistically bump the local count so the crew member's tap
      // never feels lost. The queue silently flushes when the browser
      // is back online (or on the 30s tick further down).
      const status = e?.response?.status;
      const isNetworkOrServer = !status || status >= 500 || e?.message === "Network Error";
      if (isNetworkOrServer) {
        enqueueTap({ crew_id: myId, date: today, action });
        // Optimistic UI: bump my own count locally so the +1 visibly
        // registers. We DON'T touch milestones / leaderboards / store
        // capture rate because those need the authoritative server
        // numbers — they'll catch up the next poll after sync.
        setTallyData((prev) => {
          if (!prev) return prev;
          const cur = prev.crew_counts?.[myId] || 0;
          const next = action === "add" ? cur + 1 : Math.max(0, cur - 1);
          // Same instant-wake broadcast used on the online path so the
          // HomePage Collect Emails tile flips from snoozing → dancing
          // even when we only have the optimistic local count.
          try {
            window.dispatchEvent(new CustomEvent("wow:tally-updated", {
              detail: { crew_id: myId, my_count: next, action, optimistic: true },
            }));
          } catch { /* ignore */ }
          return {
            ...prev,
            crew_counts: { ...(prev.crew_counts || {}), [myId]: next },
            my_count: next,
          };
        });
        setQueueSize(getQueueSize());
        toast(`${action === "add" ? "+1" : "−1"} saved — will sync when online`, {
          duration: 1500,
          icon: "📡",
        });
      } else {
        const detail = e?.response?.data?.detail || e?.message || "Save failed — tap again";
        toast.error(detail);
      }
    } finally {
      setTapping(false);
    }
  };

  const triggerCelebration = (message, type) => {
    setShowCelebration({ message, type });
    setTimeout(() => setShowCelebration(null), 4000);
  };

  const myCount = tallyData?.crew_counts?.[myId] || tallyData?.my_count || 0;
  const totalEmails = tallyData?.total_emails || 0;
  const storeTx = tallyData?.store_transactions || 0;
  const captureRate = tallyData?.capture_rate || 0;
  const goalRate = tallyData?.goal_rate || 0.35;
  const stretchRate = tallyData?.stretch_goal_rate || 0.50;
  const emailsNeededGoal = tallyData?.emails_needed_goal || 0;
  const emailsNeededStretch = tallyData?.emails_needed_stretch || 0;
  const atGoal = tallyData?.at_goal || false;
  const atStretch = tallyData?.at_stretch || false;
  const latestCheckpoint = tallyData?.latest_checkpoint;

  // All crew with today's counts — keep the full roster (incl. zeros) so the
  // "Working Today" strip always shows a team at a glance, even at the start
  // of a shift when nobody has tapped yet. The leaderboard (`crewWithTallies`)
  // still filters to count > 0 so we don't clutter the rank list.
  //
  // 2026-05-26: District Managers are filtered out — they're staff but they
  // don't collect emails on the floor of any one store. Listing them on
  // every store's Tally + leaderboard was confusing. They remain visible
  // on the Wow Crew (Team) page. (User report: "Erin is showing up in
  // manager dashboard edit crew tallies, she is DM, shouldn't be on any
  // store roster for email collects".)
  const crewCounts = tallyData?.crew_counts || {};
  // Merge in visiting crew — they appear as synthesized rows with a
  // small "Visiting" badge (see the crew card below). Their tally
  // counts also come from `crewCounts` because every tally +1 through
  // POST /api/tally?store_id=<THIS store> is scoped to this store's
  // day doc regardless of the crew's home store.
  const crewWithVisitors = useMemo(() => {
    const knownIds = new Set((crew || []).map((c) => c.id));
    const visitorRows = (visitors || [])
      .filter((v) => !knownIds.has(v.crew_id))
      .map((v) => ({
        id: v.crew_id,
        first_name: v.crew_name,
        home_store_code: v.home_store_code,
        home_store_id: v.home_store_id,
        active: true,
        is_visitor: true,
      }));
    return [...(crew || []), ...visitorRows];
  }, [crew, visitors]);
  const allActiveCrewWithCounts = useMemo(() => {
    return crewWithVisitors
      .filter((c) => c.active && !c.is_district_manager)
      .map((c) => ({ ...c, count: crewCounts[c.id] || 0 }))
      .sort((a, b) => b.count - a.count || (a.first_name || "").localeCompare(b.first_name || ""));
  }, [crewWithVisitors, crewCounts]);
  const crewWithTallies = useMemo(
    () => allActiveCrewWithCounts.filter((c) => c.count > 0),
    [allActiveCrewWithCounts],
  );

  const sortedCheckpoints = useMemo(() => {
    const order = { "2pm": 1, "5pm": 2, "8pm": 3 };
    return [...checkpointInfo].sort((a, b) => (order[a.checkpoint] || 0) - (order[b.checkpoint] || 0));
  }, [checkpointInfo]);

  const progressToGoal = storeTx > 0 ? Math.min(100, (captureRate / goalRate) * 100) : 0;
  const progressToStretch = storeTx > 0 ? Math.min(100, (captureRate / stretchRate) * 100) : 0;

  // WTD standings
  const wtdCrew = wtdData?.crew_wtd || [];

  // Pace indicator — compare each crew member's count today vs the team's
  // average. At or above average → "🚀 On pace". Less than half the average
  // (and team_avg is meaningful, >2) → "⚠️ Slow start". Middle pack gets no
  // badge so we don't shame anyone who's simply having a steady day.
  // Exposed as a helper so the mobile strip and the desktop list can share logic.
  const teamAvgToday = useMemo(() => {
    if (!crewWithTallies.length) return 0;
    const total = crewWithTallies.reduce((sum, m) => sum + (m.count || 0), 0);
    return total / crewWithTallies.length;
  }, [crewWithTallies]);

  const getPace = useCallback((count) => {
    if (teamAvgToday <= 0 || crewWithTallies.length < 2) return null;
    if (count >= teamAvgToday) return "on";
    if (teamAvgToday > 2 && count < teamAvgToday * 0.5) return "slow";
    return null;
  }, [teamAvgToday, crewWithTallies.length]);

  // Store closed lockout: derive from current MST time + hours, OR from an
  // explicit `closed_dates` entry (holiday / emergency closure set by a
  // manager). The holiday lock wins over both time-of-day and the actually-
  // open window so crew see a clear banner instead of a silent disable.
  // Honors per-day overrides (e.g. Sunday early close) by using
  // `effective_today` if present in the settings payload — falls back to
  // the legacy top-level fields for backwards compatibility.
  const nowMst = getMSTNow();
  const todayHours = storeHours?.effective_today
    ? { ...storeHours, ...storeHours.effective_today }
    : storeHours;
  const shiftWindow = getShiftWindow(nowMst.totalSec, todayHours);
  const holidayReason = storeHours?.effective_today?.closure_reason
    || (storeHours?.closed_dates || {})[today]
    || null;
  const storeClosed = shiftWindow.isClosed || Boolean(holidayReason);
  const beforeOpen = !holidayReason && (shiftWindow.beforeOpen || false);
  const openLabelShort = formatTimeLabel(todayHours?.open_time || "09:00");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="crew-tally-page">
      {/* One-shot yellow lightning sweep overlay — fires on the team's
          first daily-goal hit each day. Re-keying via streakKey forces
          a fresh mount so the 700ms animation runs cleanly each time. */}
      {streakKey > 0 && fbThemeActive && (
        <div key={`streak-${streakKey}`} className="fb-lightning-streak" aria-hidden data-testid="goal-lightning-streak" />
      )}
      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="celebration-overlay">
          <div className={`p-8 rounded-2xl shadow-2xl text-center max-w-sm mx-4 pointer-events-auto animate-bounce ${
            showCelebration.type === "stretch"
              ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white"
              : showCelebration.type === "goal"
              ? "bg-gradient-to-br from-emerald-400 to-emerald-600 text-white"
              : "bg-white border-2 border-red-200 text-neutral-900"
          }`}>
            <p className="text-xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
              {showCelebration.message}
            </p>
          </div>
        </div>
      )}

      {/* Header — frosted blur + brand-blue page label, matching the new
          modern HomePage shell. */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl"
        style={{
          backgroundColor: "rgba(248,250,252,0.85)",
          borderBottom: "1px solid rgba(148,163,184,0.18)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-14">
          <div className="min-w-0">
            <p
              className="text-[10px] uppercase tracking-[0.18em] font-black text-blue-600 leading-tight"
              data-testid="page-label"
            >
              Collect Emails
            </p>
            <p className="font-black text-slate-900 leading-tight truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{myName}</p>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold" data-testid="user-role-label">
              {user?.position || (user?.is_manager ? "Manager" : "Wow Crew")}
            </p>
          </div>
          {/* Consolidated icon cluster — single rounded white shell hugs all
              three actions. Cleaner than three separate pill buttons. */}
          <div
            className="flex items-center gap-1 bg-white rounded-full p-1 shadow-sm"
            style={{ border: "1px solid rgba(148,163,184,0.18)" }}
          >
            <CouponHeaderPill />
            {/* Daily Passcode pill — replaces the old in-page card. Opens
                the same slide-up sheet the Home tile uses. Renders a
                static Barcode icon regardless of claimed state; the
                visual claimed/scanned signal already lives inside the
                sheet content itself. */}
            <button
              onClick={() => setPasscodeSheetOpen(true)}
              className="w-9 h-9 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center"
              data-testid="header-passcode-button"
              aria-label="Daily Passcode"
              title="Daily Passcode"
              type="button"
            >
              <Barcode weight="bold" className="w-4 h-4 text-slate-700" />
            </button>
            {onGoHome && (
              <button onClick={onGoHome} className="w-9 h-9 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center" data-testid="home-button" aria-label="Home" title="Home">
                <House weight="bold" className="w-4 h-4 text-slate-700" />
              </button>
            )}
            {onSwitchToManager && (
              <button onClick={onSwitchToManager} className="w-9 h-9 rounded-full hover:bg-blue-50 transition-colors flex items-center justify-center" data-testid="switch-to-manager" aria-label="Manager Dashboard" title="Manager Dashboard">
                <ChartLineUp weight="bold" className="w-4 h-4 text-blue-600" />
              </button>
            )}
            <button onClick={onLogout} className="w-9 h-9 rounded-full hover:bg-rose-50 transition-colors flex items-center justify-center" data-testid="logout-button" aria-label="Sign out" title="Sign out">
              <SignOut weight="bold" className="w-4 h-4 text-rose-600" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* Holiday / emergency closure banner — shown at the very top so crew
            know immediately that captures are locked for the day. The tally
            buttons are disabled separately via `storeClosed`. */}
        {holidayReason && (
          <div
            className="mb-4 rounded-lg border-2 border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3"
            data-testid="closed-today-banner"
          >
            <Lock weight="bold" className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-black uppercase tracking-wider text-rose-900" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                Store closed today
              </p>
              <p className="text-xs text-rose-800 mt-0.5">{holidayReason}. Email captures are paused and will resume tomorrow.</p>
            </div>
          </div>
        )}
        {/* Daily Passcode moved to the header — pill icon (Barcode) opens
            the sheet. The component still mounts here in headless mode so
            its scan / claim / barcode display logic continues to work,
            but it renders nothing until the header pill is tapped. */}
        <DailyPasscodeCard
          user={user}
          hideBar
          open={passcodeSheetOpen}
          onOpenChange={setPasscodeSheetOpen}
        />
        {/* Mobile-only top-5 WTD strip. Equal 5-column grid so the strip matches the tally
            card's full width and each tile sizes to the screen. */}
        {wtdCrew.length > 0 && (
          <div className="lg:hidden mb-4" data-testid="wtd-strip-mobile">
            <div className="flex items-center gap-1.5 mb-2">
              <Trophy weight="bold" className="w-4 h-4 text-amber-500" />
              <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Top 5 This Week</span>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              {Array.from({ length: 5 }).map((_, idx) => {
                const member = wtdCrew[idx];
                if (!member) {
                  // Placeholder tile keeps the grid evenly sized when fewer than 5 crew members have WTD totals.
                  return (
                    <div
                      key={`empty-${idx}`}
                      className="rounded-xl border border-dashed border-slate-200 bg-white/60 flex items-center justify-center py-2 text-[10px] text-slate-300"
                      data-testid={`wtd-strip-rank-${idx + 1}-empty`}
                    >—</div>
                  );
                }
                const isMe = member.crew_id === myId;
                return (
                  <div
                    key={member.crew_id}
                    className={`rounded-xl border px-1 py-2 flex flex-col items-center text-center ${isMe ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white"}`}
                    data-testid={`wtd-strip-rank-${idx + 1}`}
                  >
                    <div className="relative mb-1">
                      {member.photo_url ? (
                        <img src={member.photo_url} alt={member.first_name} className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
                      ) : (
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-black text-white shadow-sm" style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                          {(member.first_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className={`absolute -bottom-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ring-2 ring-white ${getRankBadgeClass(idx)}`}>{idx + 1}</span>
                      {idx === 0 && (
                        <Crown weight="fill" className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 text-amber-500 drop-shadow" aria-label="#1" />
                      )}
                    </div>
                    <p className={`text-[11px] font-semibold leading-tight truncate w-full ${isMe ? "text-red-700" : "text-slate-800"}`} style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                      {member.first_name}
                    </p>
                    <p className="text-lg font-black leading-none mt-0.5" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                      {member.wtd_emails}
                    </p>
                  </div>
                );
              })}
            </div>
            {/* "You're #N" callout — only shown on mobile when the viewer is
                NOT already visible in the top-5 strip above. Gives lower-
                ranked crew a concrete picture of where they stand and how
                many emails they need to climb a spot (user feedback
                2026-02-22 "the team can't see where they stand"). */}
            {(() => {
              const myIdx = wtdCrew.findIndex((m) => m.crew_id === myId);
              if (myIdx < 5 || myIdx === -1) return null;
              const me = wtdCrew[myIdx];
              const ahead = wtdCrew[myIdx - 1];
              const gap = (ahead?.wtd_emails || 0) - (me.wtd_emails || 0);
              return (
                <div
                  className="mt-2 rounded-xl bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 px-3 py-2 flex items-center gap-3"
                  data-testid="my-rank-callout-mobile"
                >
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-blue-700 font-black ring-2 ring-blue-200 text-sm"
                    style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                    data-testid="my-rank-number"
                  >
                    #{myIdx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-blue-700/80">You're #{myIdx + 1} of {wtdCrew.length}</p>
                    <p className="text-xs text-slate-700 truncate">
                      {gap > 0
                        ? <>{gap} more to pass <span className="font-bold">{ahead.first_name}</span></>
                        : "Tied with the spot above — push for #1!"}
                    </p>
                  </div>
                  <span
                    className="text-2xl font-black leading-none text-blue-700"
                    style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}
                  >
                    {me.wtd_emails || 0}
                  </span>
                </div>
              );
            })()}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-5 lg:items-start">
          {/* Tally column */}
          <div className="lg:col-span-2 lg:sticky lg:top-20">
            <div
              className="relative text-center py-10 lg:py-14 rounded-3xl overflow-hidden"
              style={{
                background: storeClosed
                  ? "linear-gradient(160deg,#1E293B 0%,#0F172A 100%)"
                  : "linear-gradient(160deg,var(--fb-blue-dark) 0%,var(--fb-blue) 50%,#1E40AF 100%)",
                color: "#FFFFFF",
                boxShadow: "0 20px 50px -25px rgba(0,72,179,0.55)",
              }}
              data-testid="tally-panel"
            >
              {/* Soft yellow + pink decorative blobs to hint at the FB brand
                  without dominating the count. */}
              <span
                className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none opacity-20 blur-2xl"
                style={{ backgroundColor: "#FACC15" }}
                aria-hidden
              />
              <span
                className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none opacity-20 blur-2xl"
                style={{ backgroundColor: "#EC4899" }}
                aria-hidden
              />

              <p className="relative z-10 text-[10px] uppercase tracking-[0.22em] font-black text-white/80 mb-2">Your Emails Today</p>
              {/* Offline-queue pill — only shows when there are unsynced
                  taps. Lets the crew know the device is buffering and the
                  count is real even if wifi is spotty. */}
              {queueSize > 0 && (
                <div
                  className="relative z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 ring-1 ring-white/30 text-[10px] font-bold uppercase tracking-wider mb-3 animate-fade-in"
                  data-testid="capture-queue-pill"
                  title="Pending captures will sync when you're back online"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
                  📡 {queueSize} pending · syncs when online
                </div>
              )}
              <p
                className="relative z-10 text-8xl lg:text-9xl font-black leading-none mb-8 lg:mb-10 tabular-nums"
                style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", letterSpacing: "-0.04em" }}
                data-testid="my-email-count"
              >
                {/* Re-keyed span so React mounts a fresh DOM node every time
                    the count changes — re-triggers the roll-up animation
                    without us having to manage classList toggling. Skip the
                    animation under May 4 so the deep-space theme stays
                    crisp. (User ask 2026-05-04 odometer-style count roll.) */}
                {fbThemeActive ? (
                  <span key={myCount} className="fb-count-roll inline-block" data-testid="my-email-count-value">{myCount}</span>
                ) : (
                  <span data-testid="my-email-count-value">{myCount}</span>
                )}
              </p>

              {/* Floating "+1" / "WOW!" pops rise off the +1 button in the
                  FB default theme. Mounted on the tally panel so the rise
                  isn't clipped by the button's circular bounds. */}
              {fbThemeActive && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-[120px] lg:bottom-[160px] w-0 h-0 z-20 pointer-events-none">
                  <FloatingPops instanceRef={popsRef} />
                </div>
              )}

              <div className="relative z-10 flex items-center justify-center gap-5 lg:gap-7">
                <button
                  onPointerDown={() => { if (!storeClosed && myCount > 0 && !tapping) tap("light"); }}
                  onClick={() => handleTally("remove")}
                  disabled={tapping || myCount === 0 || storeClosed}
                  className="w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center transition-all active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(10px)",
                    border: "1.5px solid rgba(255,255,255,0.25)",
                  }}
                  data-testid="tally-minus-button"
                  aria-label="Remove one email"
                >
                  <Minus weight="bold" className="w-7 h-7 lg:w-9 lg:h-9 text-white" />
                </button>
                <div className={!may4Active && !atGoal && emailsNeededGoal > 0 && emailsNeededGoal <= 5 ? "fb-near-goal-wrap" : ""} data-testid={!may4Active && !atGoal && emailsNeededGoal > 0 && emailsNeededGoal <= 5 ? "near-goal-rim" : undefined}>
                <button
                  onPointerDown={() => { if (!storeClosed && !tapping) tap("success"); }}
                  onClick={(e) => {
                    if (storeClosed || tapping) { handleTally("add"); return; }
                    if (may4Active) {
                      // One-shot glitch overlay: red "−1" flickers across the
                      // button for ~340ms, then settles. The tally call still
                      // fires synchronously so the count updates immediately.
                      setPlusGlitching(true);
                      setTimeout(() => setPlusGlitching(false), 340);
                    } else {
                      // FB default theme: spawn a floating "+1" / "WOW!" pop
                      // and a micro confetti spray rooted at the button's
                      // viewport center so the celebration feels physical.
                      try { popsRef.current?.trigger(); } catch { /* ignore */ }
                      try {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = (rect.left + rect.width / 2) / window.innerWidth;
                        const y = (rect.top + rect.height / 2) / window.innerHeight;
                        microConfetti(x, y);
                      } catch { /* ignore — confetti is purely cosmetic */ }
                    }
                    handleTally("add");
                  }}
                  disabled={tapping || storeClosed}
                  className={`relative w-32 h-32 lg:w-40 lg:h-40 rounded-full flex items-center justify-center transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group ${plusGlitching ? "may4-plus-glitching" : ""}`}
                  style={{
                    background: "linear-gradient(160deg,#FACC15 0%,#F59E0B 100%)",
                    boxShadow: storeClosed
                      ? "none"
                      : "0 14px 30px -10px rgba(250,204,21,0.55), inset 0 -3px 0 rgba(0,0,0,0.08), inset 0 2px 0 rgba(255,255,255,0.4)",
                  }}
                  data-testid="tally-plus-button"
                  aria-label="Add one email"
                >
                  <Plus weight="bold" className={`w-14 h-14 lg:w-20 lg:h-20 text-white drop-shadow group-active:scale-90 transition-transform ${plusGlitching ? "may4-plus-icon-glitch" : ""}`} />
                  {/* May the 4th glitch overlay — only used when that theme
                      is on. The FB-default floating pops live OUTSIDE the
                      button (rooted on the parent panel) so they can rise
                      past the button's circular clip. */}
                  {plusGlitching && (
                    <span
                      className="may4-plus-minus-flash absolute inset-0 flex items-center justify-center pointer-events-none"
                      aria-hidden
                      data-testid="tally-plus-glitch"
                    >
                      <span className="text-5xl lg:text-7xl font-black text-rose-500 leading-none select-none" style={{ textShadow: "2px 0 #06b6d4, -2px 0 #f43f5e" }}>
                        −1
                      </span>
                    </span>
                  )}
                </button>
                </div>
                <div className="w-16 h-16 lg:w-20 lg:h-20" aria-hidden />
              </div>
              <p className="relative z-10 text-sm mt-5 lg:mt-7 px-4" data-testid="tally-hint">
                {holidayReason ? (
                  <span className="text-rose-200 font-semibold" data-testid="closed-holiday-hint">Store closed today — {holidayReason}</span>
                ) : storeClosed ? (
                  beforeOpen ? (
                    <span className="text-amber-200 font-semibold">Store opens at {openLabelShort} — captures unlock then.</span>
                  ) : (
                    <span className="text-rose-200 font-semibold">Store is closed — captures are locked until tomorrow.</span>
                  )
                ) : (
                  <span className="text-white/85">
                    Tap the big <span className="font-black text-white">+</span> every time you capture an email
                  </span>
                )}
              </p>
            </div>

            {/* Heart Balloon quick-add chip removed 2026-05-19 with the campaign cleanup. */}

            {/* Mobile-only Working Today strip — sits directly under the
                "Your Emails Today" tally card so crew immediately see how
                they're pacing vs the rest of the team right after tapping +.
                Only shows crew who have ACTUALLY captured emails today
                (count > 0) — we don't surface zero-tally names (user ask
                2026-05-02). Desktop hides this in favor of the full list
                in the right column. */}
            {crewWithTallies.length > 0 && (
              <div className="lg:hidden mt-4" data-testid="working-today-strip-mobile">
                <div className="flex items-center justify-between gap-1.5 mb-2">
                  <div className="flex items-center gap-1.5">
                    <Users weight="bold" className="w-4 h-4 text-slate-600" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Working Today</span>
                  </div>
                  <div className="flex items-center gap-1" data-testid="working-today-total-mobile">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Team</span>
                    <span
                      className="text-sm font-black leading-none"
                      style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "var(--fb-blue, var(--fb-blue))" }}
                    >
                      {crewWithTallies.reduce((sum, m) => sum + (m.count || 0), 0)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {Array.from({ length: 5 }).map((_, idx) => {
                    const member = crewWithTallies[idx];
                    if (!member) {
                      return (
                        <div
                          key={`working-empty-${idx}`}
                          className="rounded-xl border border-dashed border-slate-200 bg-white/60 flex items-center justify-center py-2 text-[10px] text-slate-300"
                          data-testid={`working-today-rank-${idx + 1}-empty`}
                        >—</div>
                      );
                    }
                    const isMe = member.id === myId;
                    const isLeader = idx === 0;
                    const pace = getPace(member.count || 0);
                    return (
                      <div
                        key={member.id}
                        className={`rounded-xl border px-1 py-2 flex flex-col items-center text-center ${isMe ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}
                        data-testid={`working-today-rank-${idx + 1}`}
                      >
                        <div className="relative mb-1">
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.first_name} className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
                          ) : (
                            <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-black text-white shadow-sm" style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                              {(member.first_name || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className={`absolute -bottom-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ring-2 ring-white ${getRankBadgeClass(idx)}`}>{idx + 1}</span>
                          {isLeader && (
                            <Crown weight="fill" className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 text-amber-500 drop-shadow" aria-label="Top of the day" />
                          )}
                        </div>
                        <p className={`text-[11px] font-semibold leading-tight truncate w-full ${isMe ? "text-emerald-700" : "text-slate-800"}`} style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                          {member.first_name}
                        </p>
                        <p className="text-lg font-black leading-none mt-0.5" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                          {member.count}
                        </p>
                        {pace && (
                          <span
                            className="text-[11px] leading-none mt-0.5"
                            title={pace === "on" ? "On pace — at or above team average" : "Slow start — below half the team average"}
                            aria-label={pace === "on" ? "On pace" : "Slow start"}
                            data-testid={`pace-${member.id}`}
                          >
                            {pace === "on" ? "🚀" : "⚠️"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right column: progress + standings */}
          <div className="lg:col-span-3 space-y-4">
            {/* Store Progress */}
            <div className="dashboard-card" data-testid="store-progress">
          <div className="flex items-center gap-2 mb-3">
            <Target weight="bold" className="w-5 h-5" style={{ color: "var(--fb-blue)" }} />
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Store Progress</span>
          </div>

          {storeTx === 0 ? (
            <p className="text-slate-500 text-sm">Waiting for manager to enter transaction count...</p>
          ) : (
            <>
              {latestCheckpoint && (
                <p className="text-xs text-slate-400 mb-2">
                  Based on {latestCheckpoint.toUpperCase()} reading: {storeTx} transactions
                </p>
              )}
              <div className="flex items-end justify-between mb-2">
                <div>
                  <p className="text-3xl font-black" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{(captureRate * 100).toFixed(1)}%</p>
                  <p className="text-sm text-slate-500">{totalEmails} emails / {storeTx} transactions</p>
                </div>
                <div className="text-right">
                  {atGoal ? (
                    <span className="text-emerald-600 font-bold text-lg">Goal Hit!</span>
                  ) : (
                    <div>
                      <p className="text-2xl font-black text-amber-500" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }} data-testid="emails-needed">{emailsNeededGoal}</p>
                      <p className="text-xs text-slate-500">more to {(goalRate * 100).toFixed(0)}%</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500">Goal: {(goalRate * 100).toFixed(0)}%</span>
                    <span className={atGoal ? "text-emerald-600 font-semibold" : "text-amber-600 font-semibold"}>{progressToGoal.toFixed(0)}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className={`progress-bar-fill ${getProgressClass(atGoal, captureRate)}`} style={{ width: `${progressToGoal}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-500 flex items-center gap-1">
                      <Lightning weight="fill" className="w-3 h-3 text-amber-500" /> Stretch: {(stretchRate * 100).toFixed(0)}%
                    </span>
                    <span className={atStretch ? "text-emerald-600 font-semibold" : "text-slate-500"}>
                      {atStretch ? "Crushed it!" : `${emailsNeededStretch} more`}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className={`progress-bar-fill ${atStretch ? "at-goal" : "bg-neutral-400"}`} style={{ width: `${progressToStretch}%` }} />
                  </div>
                </div>
              </div>
              {latestCheckpoint && (
                <div className="text-xs text-slate-400 mt-3 pt-2 border-t border-slate-100 space-y-1">
                  {sortedCheckpoints.map((cp) => (
                    <p key={cp.checkpoint}>
                      <span className="font-semibold text-slate-500">{cp.checkpoint.toUpperCase()}</span>: {cp.store_transactions} tx
                      {cp.updated_by_name && (
                        <span> - by <span className="font-medium" style={{ color: "var(--fb-blue)" }}>{cp.updated_by_name}</span></span>
                      )}
                      {cp.updated_at && (
                        <span className="ml-1">at {new Date(cp.updated_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                      )}
                    </p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Crew Working Today (only those with tallies) — desktop only. Mobile
            sees the compact horizontal strip directly under the Top-5 WTD strip
            at the top of <main>. */}
        {crewWithTallies.length > 0 && (
          <div className="dashboard-card hidden lg:block" data-testid="crew-live-counts">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Users weight="bold" className="w-5 h-5 text-slate-600" />
                <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Working Today</span>
              </div>
              {/* Total emails across every Wow Crew member working today — quick
                  at-a-glance "how are we doing as a team" metric. */}
              <div className="flex items-center gap-1.5" data-testid="working-today-total">
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Team</span>
                <span
                  className="text-lg font-black leading-none"
                  style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif", color: "var(--fb-blue, var(--fb-blue))" }}
                >
                  {crewWithTallies.reduce((sum, m) => sum + (m.count || 0), 0)}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {crewWithTallies.map((member, idx) => {
                const isMe = member.id === myId;
                const isLeader = idx === 0;
                const pace = getPace(member.count || 0);
                return (
                  <div
                    key={member.id}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg transition-all ${
                      isMe
                        ? "bg-emerald-50 border border-emerald-200"
                        : isLeader
                          ? "bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200"
                          : "hover:bg-slate-50"
                    }`}
                    data-testid={`crew-rank-${member.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${getRankBadgeClass(idx)}`}>{idx + 1}</span>
                      <div className="relative flex-shrink-0">
                        {member.photo_url ? (
                          <img src={member.photo_url} alt={member.first_name} className="w-8 h-8 rounded-full object-cover ring-2 ring-white shadow-sm" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white shadow-sm" style={{ backgroundColor: "var(--fb-blue, var(--fb-blue))", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                            {(member.first_name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        {isLeader && (
                          <Crown
                            weight="fill"
                            className="absolute -top-2.5 -right-2 w-4 h-4 text-amber-500 drop-shadow"
                            aria-label="Top of the day"
                          />
                        )}
                      </div>
                      <span className={`font-medium truncate ${isMe ? "text-emerald-700" : ""}`}>
                        {member.first_name}
                        {isMe && <span className="text-xs text-emerald-500 ml-1">(You)</span>}
                      </span>
                      {pace && (
                        <span
                          className={`ml-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                            pace === "on"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                          title={pace === "on" ? "At or above team average" : "Below half the team average"}
                          data-testid={`pace-desktop-${member.id}`}
                        >
                          {pace === "on" ? "🚀 On pace" : "⚠️ Slow start"}
                        </span>
                      )}
                    </div>
                    <span className="font-bold text-lg flex-shrink-0" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{member.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Shift Comparison — visible to everyone. Shows 2pm → 5pm → 8pm trend
            as checkpoints land so crew can see how the store is pacing through
            the day. Renders nothing until at least two checkpoints have been
            logged. */}
        <ShiftComparisonCard date={today} />

        {/* WTD Standings — desktop only. Mobile sees the top-5 strip at the top of <main>. */}
        <div className="dashboard-card hidden lg:block" data-testid="wtd-standings">
          <button
            className="w-full flex items-center justify-between lg:cursor-default"
            onClick={() => setShowWtd(!showWtd)}
            data-testid="wtd-toggle"
          >
            <div className="flex items-center gap-2">
              <Trophy weight="bold" className="w-5 h-5 text-amber-500" />
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Week Standings</span>
            </div>
            <span className="text-xs text-slate-400 lg:hidden">{showWtd ? "Hide" : "Show"}</span>
          </button>

          {wtdCrew.length > 0 && (
            <div className={`mt-3 space-y-2 ${showWtd ? "" : "hidden lg:block"}`}>
              {wtdCrew.map((member, idx) => {
                const isLeader = idx === 0;
                return (
                  <div
                    key={member.crew_id}
                    className={`flex items-center justify-between py-2 px-3 rounded-lg transition-all ${
                      member.crew_id === myId
                        ? "bg-blue-50 border border-blue-200"
                        : isLeader
                          ? "bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200"
                          : ""
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0 ${getRankBadgeClass(idx)}`}>{idx + 1}</span>
                      <div className="relative flex-shrink-0">
                        {member.photo_url ? (
                          <img src={member.photo_url} alt={member.first_name} className="w-7 h-7 rounded-full object-cover ring-1 ring-slate-200" />
                        ) : (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: "var(--fb-blue)", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                            {(member.first_name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        {isLeader && (
                          <Crown weight="fill" className="absolute -top-2 -right-1.5 w-3.5 h-3.5 text-amber-500 drop-shadow" aria-label="Week leader" />
                        )}
                      </div>
                      <span className={`font-medium text-sm truncate ${member.crew_id === myId ? "text-red-700" : ""}`}>
                        {member.first_name}
                        {member.crew_id === myId && <span className="text-xs ml-1" style={{ color: "var(--fb-blue)" }}>(You)</span>}
                      </span>
                    </div>
                    <span className="font-bold text-sm flex-shrink-0" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{member.wtd_emails}</span>
                  </div>
                );
              })}
            </div>
          )}
          {wtdCrew.length === 0 && (
            <p className={`text-sm text-slate-400 mt-3 ${showWtd ? "" : "hidden lg:block"}`}>No data this week yet</p>
          )}
        </div>
          </div>
        </div>
      </main>
    </div>
  );
}
